import { NavLink, Outlet, Link } from "react-router-dom";
import {
    LayoutDashboard,
    Wrench,
    BookOpen,
    Newspaper,
    Tag,
    Star,
    HelpCircle,
    Inbox,
    Settings as SettingsIcon,
    LogOut,
    ExternalLink,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

const nav = [
    { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/admin/services", label: "Services", icon: Wrench, testid: "nav-services" },
    { to: "/admin/case-studies", label: "Case Studies", icon: BookOpen, testid: "nav-case-studies" },
    { to: "/admin/blog", label: "Blog Posts", icon: Newspaper, testid: "nav-blog" },
    { to: "/admin/pricing", label: "Pricing", icon: Tag, testid: "nav-pricing" },
    { to: "/admin/testimonials", label: "Testimonials", icon: Star, testid: "nav-testimonials" },
    { to: "/admin/faqs", label: "FAQs", icon: HelpCircle, testid: "nav-faqs" },
    { to: "/admin/enquiries", label: "Enquiries", icon: Inbox, testid: "nav-enquiries" },
    { to: "/admin/settings", label: "Site Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();

    return (
        <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
            {/* Sidebar */}
            <aside className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col sticky top-0 h-screen">
                <Link
                    to="/admin/dashboard"
                    className="flex items-center gap-2 px-5 py-5 border-b border-slate-200"
                >
                    <div className="w-8 h-8 bg-slate-950 text-white grid place-items-center rounded-md font-display font-black">
                        Ω
                    </div>
                    <div className="leading-tight">
                        <div className="font-display font-bold text-slate-950 text-sm">
                            Omega IT
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                            CMS Console
                        </div>
                    </div>
                </Link>

                <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                    {nav.map((n) => {
                        const Icon = n.icon;
                        return (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={n.testid}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        isActive
                                            ? "bg-slate-950 text-white"
                                            : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                                    }`
                                }
                                end
                            >
                                <Icon className="w-4 h-4" />
                                {n.label}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="p-3 border-t border-slate-200">
                    <a
                        href="/"
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 px-3 py-2 rounded-md text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-950 transition-colors"
                    >
                        <ExternalLink className="w-3.5 h-3.5" />
                        View public site
                    </a>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Topbar */}
                <header className="bg-white border-b border-slate-200 px-6 lg:px-10 h-16 flex items-center justify-between sticky top-0 z-10">
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold">
                        Omega IT Ltd · Content Management
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="text-right hidden sm:block">
                            <div className="text-sm font-semibold text-slate-950">
                                {user?.name || "Admin"}
                            </div>
                            <div className="text-xs text-slate-500" data-testid="topbar-user-email">
                                {user?.email}
                            </div>
                        </div>
                        <div className="w-9 h-9 rounded-full bg-slate-950 text-white grid place-items-center text-sm font-display font-bold">
                            {(user?.name || user?.email || "A").charAt(0).toUpperCase()}
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={logout}
                            data-testid="logout-button"
                            className="text-slate-600 hover:text-slate-950"
                        >
                            <LogOut className="w-4 h-4 mr-1.5" />
                            Logout
                        </Button>
                    </div>
                </header>

                <main className="flex-1 px-6 lg:px-10 py-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
